import { useCallback } from "react";
import styled from "@emotion/styled";
import { MdsItem, MdsItemKind } from "@/design-system/components/item-list/types";
import { CollectionIconWithToggle } from "@/design-system/components/item-list/rows/icon/collection";
import { NoteIcon } from "@/design-system/components/item-list/rows/icon/note";
import { TemplateIcon } from "@/design-system/components/item-list/rows/icon/template";
import { MDSRowItemSlotName } from "@/mds";
import { SearchIcon } from "@/design-system/components/item-list/rows/icon/search";
import { MdsIconKind } from "@/design-system/components/icon";

interface Props {
  item: MdsItem;
  isSelected?: boolean;
  isHighlighted?: boolean;
  itemRef: React.MutableRefObject<MdsItem>;
  handleItemClick: (event: React.MouseEvent) => void;
  slot?: MDSRowItemSlotName;
}

export function Icon({ item, itemRef, isSelected, isHighlighted, handleItemClick }: Props) {
  const toggleSelected = useCallback(
    (event: React.MouseEvent) => {
      if (event.shiftKey) {
        handleItemClick(event);
        return;
      }
      event.stopPropagation();
      const item = itemRef.current;
      item.listState?.toggleItemSelection(item.id);
    },
    [handleItemClick, itemRef]
  );

  return (
    <Wrapper>
      {item.kind === MdsItemKind.Collection && (
        <CollectionIconWithToggle
          itemId={item.id}
          isSelected={isSelected}
          isHighlighted={isHighlighted}
          toggleSelected={toggleSelected}
        />
      )}
      {item.kind === MdsItemKind.Note && (
        <NoteIcon
          isSelected={isSelected}
          isHighlighted={isHighlighted}
          toggleSelected={toggleSelected}
        />
      )}
      {item.kind === MdsItemKind.ActiveAudioCaptureNote && (
        <NoteIcon
          isSelected={isSelected}
          isHighlighted={isHighlighted}
          toggleSelected={toggleSelected}
          isActiveAudioCapture={true}
        />
      )}
      {item.kind === MdsItemKind.Template && (
        <TemplateIcon
          isSelected={isSelected}
          isHighlighted={isHighlighted}
          toggleSelected={toggleSelected}
        />
      )}
      {item.kind === MdsItemKind.Search && (
        <SearchIcon
          color={({ theme }) => theme.colors.icon.emphasis}
          isSelected={isSelected}
          isHighlighted={isHighlighted}
          toggleSelected={toggleSelected}
        />
      )}
      {item.kind === MdsItemKind.Message && (
        <SearchIcon
          defaultIcon={MdsIconKind.Message}
          color={({ theme }) => theme.colors.icon.emphasis}
          isSelected={isSelected}
          isHighlighted={isHighlighted}
          toggleSelected={toggleSelected}
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  min-width: fit-content;
  align-self: stretch;
  padding-right: ${({ theme }) => theme.spacing["3"]};
  padding-top: 6px;
`;
